import { getResponse } from '@satellite/services/serviceUtilities.js';
import { cachedInflightRequest } from '@nova/core';

export default {
  async createConfiguration(data, options = {}) {
    const response = await axios.post('/unit-limit/configuration', data);
    return getResponse(response, options);
  },

  /**
   * Fetches the configuration for unit limits.
   *
   * @param {Object} params - The query parameters for the request.
   * @param {string} params.warehouseId - The ID of the warehouse.
   * @param {string} params.unitGrouperType - The type of the unit grouper.
   * @param {string} params.unitGrouperId - The ID of the unit grouper.
   * @param {string} params.unitType - The type of the unit.
   * @param {Object} [options] - Optional configuration for the request.
   * @returns {Promise<Object>} - The response data.
   */
  async getConfiguration(params, options = {}) {
    const cacheKey = `unit-limit-configuration:${JSON.stringify(
      params,
      Object.keys(params).sort()
    )}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`/unit-limit/configuration`, { params });
      return getResponse(response, options) ?? [];
    });
  },

  /**
   * Fetches the count data for unit limits.
   *
   * @param {Object} params - The query parameters for the request.
   * @param {string[]} params.unitLimitIds - Array of unit limit IDs.
   * @param {string} params.countedDateStart - Start date for counting (ISO date format).
   * @param {string} params.countedDateEnd - End date for counting (ISO date format).
   * @param {Object} [options] - Optional configuration for the request.
   * @returns {Promise<Object>} - The response data.
   */
  async getCount(params, options = {}) {
    const cacheKey = `unit-limit-count:${JSON.stringify(params, Object.keys(params).sort())}`;
    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get('/unit-limit/count', { params });
      return getResponse(response, options) ?? [];
    });
  },

  /**
   * Validates unit limits for a given date range.
   *
   * @param {Object} data - The validation request data.
   * @param {string} data.warehouseId - The ID of the warehouse.
   * @param {string} data.dockId - The ID of the dock.
   * @param {string} data.loadTypeId - The ID of the load type.
   * @param {string} data.start - Start date (ISO date format, e.g., '2025-11-20').
   * @param {string} data.end - End date (ISO date format, must be within 3 days of start).
   * @param {Object} [options] - Optional configuration for the request.
   * @returns {Promise<Array>} - Array of validation results per day, e.g., [{ valid: true, message: string, date: string }]
   */
  async validate(data, options = {}) {
    const response = await axios.post('/unit-limit/validate', data);
    return getResponse(response, options) ?? [];
  }
};
